// F1 Team Detail Page
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useF1Championship } from '../../hooks/useF1Championship';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart } from '../../components/charts';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getTeamConstructorIds, getTeamInfo } from '../../data/teamAliases';

const F1TeamDetailPage = () => {
  const { constructorId } = useParams();
  const { data, loading, error } = useF1Championship();

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <LoadingSpinner message="Loading Team Data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-red-600 mb-4">Error Loading Team</div>
          <div className="theme-secondary">{error}</div>
          <Link to="/f1/teams" className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Teams
          </Link>
        </div>
      </div>
    );
  }

  // Find specific constructor
  const constructor = data?.constructors?.find(c => c.constructorId == constructorId);
  
  // Get all constructor IDs for this team (including aliases)
  const teamConstructorIds = getTeamConstructorIds(constructorId);
  const teamInfo = getTeamInfo(constructorId);
  
  if (!constructor) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Team Not Found</div>
          <div className="theme-secondary mb-4">Team with ID {constructorId} could not be found.</div>
          <Link to="/f1/teams" className="inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Teams
          </Link>
        </div>
      </div>
    );
  }

  // Get races where this constructor (and its aliases) participated
  const constructorRaces = data?.races?.filter(race => {
    // Check if any of the team's constructor IDs had results in this race
    return data?.results?.some(result => 
      teamConstructorIds.includes(result.constructorId) && result.raceId === race.raceId
    );
  }) || [];

  // Get detailed results for this constructor (including all aliases)
  const constructorResults = data?.results?.filter(result => 
    teamConstructorIds.includes(result.constructorId)
  ) || [];

  // Get podium finishes for this constructor (including all aliases)
  const podiumResults = constructorResults.filter(result => result.position >= 1 && result.position <= 3);

  // Get wins for this constructor (including all aliases)
  const winResults = constructorResults.filter(result => result.position === 1);

  // Get pole positions for this constructor (including all aliases)
  const poleResults = data?.qualifying?.filter(qual => 
    teamConstructorIds.includes(qual.constructorId) && qual.position === 1
  ) || [];

  // Transform race data for display
  const transformedRaces = constructorRaces.map(race => {
    const raceResult = constructorResults.find(result => result.raceId === race.raceId);
    const qualifyingResult = data?.qualifying?.find(qual => 
      qual.raceId === race.raceId && teamConstructorIds.includes(qual.constructorId)
    );
    
    return {
      ...race,
      position: raceResult?.position || 'DNF',
      points: raceResult?.points || 0,
      grid: qualifyingResult?.position || 'N/A',
      laps: raceResult?.laps || 0,
      status: raceResult?.status || 'Unknown'
    };
  });

  const stats = [
    { value: constructor.name, label: 'Team Name', color: 'theme-primary' },
    { value: constructor.nationality, label: 'Nationality', color: 'theme-primary' },
    { value: constructor.championships || 0, label: 'Championships', color: 'theme-primary' },
    { value: constructor.wins || 0, label: 'Total Wins', color: 'theme-primary' },
    { value: constructor.poles || 0, label: 'Pole Positions', color: 'theme-primary' },
    { value: constructorRaces.length, label: 'Races Entered', color: 'theme-primary' },
    { value: podiumResults.length, label: 'Podium Finishes', color: 'theme-primary' },
    { value: winResults.length, label: 'Race Wins', color: 'theme-primary' }
  ];

  return (
    <F1PageTemplate
      title={constructor.name}
      subtitle="Team information, performance history, and race results"
      summary={['Team statistics', 'Race history', 'Performance analysis']}
      stats={stats}
    >
      <div className="space-y-8">
        {/* Team Information */}
        <div className="card">
          <h3 className="text-xl font-semibold theme-primary mb-4">Team Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <span className="font-medium theme-secondary">Constructor ID:</span>
              <span className="ml-2 theme-primary">{constructor.constructorId}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Team Name:</span>
              <span className="ml-2 theme-primary">{constructor.name}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Nationality:</span>
              <span className="ml-2 theme-primary">{constructor.nationality}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Championships:</span>
              <span className="ml-2 theme-primary">{constructor.championships || 0}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Total Wins:</span>
              <span className="ml-2 theme-primary">{constructor.wins || 0}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Pole Positions:</span>
              <span className="ml-2 theme-primary">{constructor.poles || 0}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">URL:</span>
              <span className="ml-2 theme-primary">
                {constructor.url ? (
                  <a href={constructor.url} target="_blank" rel="noopener noreferrer" className="theme-accent hover:underline">
                    Wikipedia
                  </a>
                ) : 'Unknown'}
              </span>
            </div>
          </div>
          
          {/* Team Aliases */}
          {teamInfo && teamInfo.aliases.length > 1 && (
            <div className="mt-6 pt-6 border-t theme-border">
              <span className="font-medium theme-secondary block mb-2">Historical Team Names:</span>
              <div className="flex flex-wrap gap-2">
                {teamInfo.aliases.map((alias, index) => (
                  <span 
                    key={index} 
                    className="px-3 py-1 bg-[var(--theme-accent)] text-[var(--theme-accent-contrast)] rounded-full text-sm"
                  >
                    {alias}
                  </span>
                ))}
              </div>
              <p className="text-sm theme-secondary mt-2">
                Results include all historical team names and rebranding shown above.
              </p>
            </div>
          )}
        </div>

        {/* Race History */}
        {constructorRaces.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold theme-primary mb-4">Race History</h3>
            <p className="theme-secondary mb-4">
              All Formula 1 races where {constructor.name} participated, including finishing positions and points scored.
            </p>
            <TableChart 
              data={transformedRaces}
              config={{
                columns: [
                  { key: 'year', label: 'Year' },
                  { key: 'round', label: 'Round' },
                  { key: 'name', label: 'Grand Prix' },
                  { key: 'grid', label: 'Grid' },
                  { key: 'position', label: 'Finish' },
                  { key: 'points', label: 'Points' },
                  { key: 'laps', label: 'Laps' }
                ],
                sortable: true,
                sortBy: 'year',
                sortDirection: 'desc',
                pagination: true,
                pageSize: 20
              }}
            />
          </div>
        )}

        {/* Performance Summary */}
        <div className="card">
          <h3 className="text-xl font-semibold theme-primary mb-4">Performance Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold theme-primary mb-2">
                {winResults.length}
              </div>
              <div className="text-sm theme-secondary">Race Wins</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold theme-primary mb-2">
                {podiumResults.length}
              </div>
              <div className="text-sm theme-secondary">Podium Finishes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold theme-primary mb-2">
                {poleResults.length}
              </div>
              <div className="text-sm theme-secondary">Pole Positions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold theme-primary mb-2">
                {constructorRaces.length > 0 ? ((winResults.length / constructorRaces.length) * 100).toFixed(1) : 0}%
              </div>
              <div className="text-sm theme-secondary">Win Rate</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="card">
          <div className="flex justify-between items-center">
            <Link to="/f1/teams" className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
              ← Back to All Teams
            </Link>
            <div className="text-sm theme-secondary">
              Constructor ID: {constructorId}
            </div>
          </div>
        </div>
      </div>
    </F1PageTemplate>
  );
};

export default F1TeamDetailPage;
